/**
 * Empleados — quién atiende cada orden.
 *
 * En la orden se escribía a mano y el texto libre en un dato que se repite
 * es historia que se pierde. Con la lista se puede saber cuánto atendió cada
 * persona.
 *
 * Las pestañas de vehículos y marcas eran del lavadero y quedaron ocultas
 * (`CON_VEHICULOS`): lo atendido ahora lo define cada tipo de orden con sus
 * campos y su identificador (Configuración → Tipos de orden).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Stack, Alert, Tooltip,
  Switch, FormControlLabel, CircularProgress, Tabs, Tab, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import api from '../api';
import { useSede } from '../contexto/Sede';
import SelectorMarcaLinea from './SelectorMarcaLinea';
import { CampoTelefono } from './CamposContacto';
import { errorTelefono } from '../contacto';
import { useEsMovil, FILA, ancho, tablaAncha, ENCABEZADO, botonEncabezado } from '../estilos/responsivo';

const fmt = (v) => '$' + new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(Number(v || 0));
const fecha = (v) => (v ? new Date(v).toLocaleDateString('es-CO',
  { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const CON_VEHICULOS = false;

const EMPLEADO_VACIO = { nombre: '', documento: '', cargo: '', telefono: '',
                         sede_id: '', activo: true };
const VEHICULO_VACIO = { placa: '', tipo: 'Automóvil', marca: '', linea: '', color: '',
                         modelo: '', tercero_id: '', propietario: '', telefono: '',
                         notas: '', activo: true };

const TIPOS = ['Automóvil', 'Camioneta', 'Moto', 'Camión', 'Bus', 'Otro'];

export default function Maestros() {
  const esMovil = useEsMovil();
  const { sedes } = useSede();
  const [tab, setTab] = useState(0);
  const [empleados, setEmpleados] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [terceros, setTerceros] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [buscar, setBuscar] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [edicion, setEdicion] = useState(null);   // {_que:'empleado'|'vehiculo', ...}
  const [errorForm, setErrorForm] = useState('');
  const [historial, setHistorial] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [e, v, t, m] = await Promise.all([
        api.get('/empleados', { params: { incluir_inactivos: true } }),
        api.get('/vehiculos', { params: { buscar: buscar || undefined } }),
        api.get('/terceros').catch(() => ({ data: [] })),
        api.get('/marcas').catch(() => ({ data: [] })),
      ]);
      setEmpleados(e.data); setVehiculos(v.data);
      setTerceros(t.data || []); setMarcas(m.data || []);
      setError('');
    } catch (ex) { setError(ex.response?.data?.detail || 'No se pudo cargar'); }
    finally { setLoading(false); }
  }, [buscar]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarMarcas = useCallback(() => api.get('/marcas')
    .then(r => setMarcas(r.data || [])).catch(() => {}), []);

  const [intento, setIntento] = useState(false);
  const guardar = async () => {
    setIntento(true);
    if (errorTelefono(edicion.telefono)) return;
    const { _que: que, id, ...resto } = edicion;
    const cuerpo = { ...resto,
      sede_id: resto.sede_id || null,
      tercero_id: resto.tercero_id || null };
    const ruta = que === 'empleado' ? '/empleados'
      : que === 'marca' ? '/marcas' : '/vehiculos';
    try {
      if (id) await api.put(`${ruta}/${id}`, cuerpo);
      else await api.post(ruta, cuerpo);
      setEdicion(null); setErrorForm(''); cargar();
      if (que === 'marca') cargarMarcas();
    } catch (e) { setErrorForm(e.response?.data?.detail || 'No se pudo guardar'); }
  };

  const borrarEmpleado = async (e) => {
    if (!window.confirm(`¿Borrar a ${e.nombre}?`)) return;
    try { await api.delete(`/empleados/${e.id}`); cargar(); }
    catch (ex) { setError(ex.response?.data?.detail || 'No se pudo borrar'); }
  };

  const borrarMarca = async (m) => {
    if (!window.confirm(`¿Borrar la marca ${m.nombre} y sus ${m.lineas.length} líneas?`)) return;
    try { await api.delete(`/marcas/${m.id}`); cargarMarcas(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  const borrarLinea = async (m, l) => {
    if (!window.confirm(`¿Borrar ${m.nombre} ${l.nombre}?`)) return;
    try { await api.delete(`/lineas/${l.id}`); cargarMarcas(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo borrar'); }
  };

  const agregarLinea = async (m) => {
    const nombre = window.prompt(`Nueva línea de ${m.nombre}:`);
    if (!nombre || !nombre.trim()) return;
    try { await api.post('/lineas', { marca_id: m.id, nombre: nombre.trim() });
      cargarMarcas(); }
    catch (e) { setError(e.response?.data?.detail || 'No se pudo crear'); }
  };

  const verHistorial = async (v) => {
    try {
      const { data } = await api.get(`/vehiculos/${v.id}/historial`);
      setHistorial(data);
    } catch (e) { setError(e.response?.data?.detail || 'No se pudo cargar el historial'); }
  };

  return (
    <Box>
      <Box sx={ENCABEZADO}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Quién atiende cada orden. Se escoge de la lista en vez de escribirlo cada vez.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          {...botonEncabezado(esMovil)}
          onClick={() => {
            setEdicion(tab === 0 ? { ...EMPLEADO_VACIO, _que: 'empleado' }
              : tab === 1 ? { ...VEHICULO_VACIO, _que: 'vehiculo' }
                : { nombre: '', activo: true, _que: 'marca' });
            setErrorForm('');
          }}>
          {esMovil ? (tab === 2 ? 'Nueva' : 'Nuevo')
            : (tab === 0 ? 'Nuevo empleado' : tab === 1 ? 'Nuevo vehículo' : 'Nueva marca')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tab label={`Empleados (${empleados.length})`} />
          {CON_VEHICULOS && <Tab label={`Vehículos (${vehiculos.length})`} />}
          {CON_VEHICULOS && <Tab label={`Marcas y líneas (${marcas.length})`} />}
        </Tabs>

        {tab === 1 && (
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField size="small" placeholder="Placa, marca o propietario…"
              value={buscar} onChange={e => setBuscar(e.target.value)} sx={{ width: { xs: '100%', sm: 300 } }}
              InputProps={{ startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                </InputAdornment>) }} />
          </Box>
        )}

        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : tab === 0 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(760)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Empleado', 'Cargo', 'Documento', 'Teléfono', 'Sede',
                    'Órdenes', 'Estado', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {empleados.map(e => (
                  <TableRow key={e.id} hover>
                    <TableCell sx={{ fontSize: 13.5, fontWeight: 600 }}>{e.nombre}</TableCell>
                    <TableCell>
                      <Chip size="small" label={e.cargo || '—'}
                        sx={{ height: 20, fontSize: 10.5 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{e.documento || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{e.telefono || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {e.sede_nombre || <span style={{ color: '#94a3b8' }}>Todas</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{e.ordenes_atendidas}</TableCell>
                    <TableCell>
                      <Chip size="small" label={e.activo ? 'Activo' : 'Inactivo'}
                        color={e.activo ? 'success' : 'default'}
                        variant={e.activo ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => {
                          setEdicion({ ...e, _que: 'empleado', sede_id: e.sede_id || '' });
                          setErrorForm('');
                        }}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Borrar">
                        <IconButton size="small" color="error" onClick={() => borrarEmpleado(e)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : tab === 1 ? (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={tablaAncha(760)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  {['Placa', 'Vehículo', 'Propietario', 'Visitas', 'Última',
                    'Facturado', ''].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11,
                      textTransform: 'uppercase', color: '#64748b' }}
                      align={h === 'Facturado' ? 'right' : 'left'}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vehiculos.map(v => (
                  <TableRow key={v.id} hover>
                    <TableCell sx={{ fontSize: 14, fontWeight: 800,
                                     fontFamily: 'ui-monospace, monospace' }}>
                      {v.placa}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13 }}>
                        {[v.marca, v.linea].filter(Boolean).join(' ') || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                        {[v.tipo, v.color, v.modelo].filter(Boolean).join(' · ')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {v.propietario || '—'}
                      {v.telefono && (
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                          {v.telefono}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, fontWeight: 700 }}>{v.visitas}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{fecha(v.ultima_visita)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700 }}>
                      {fmt(v.total_facturado)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Historial del vehículo">
                        <IconButton size="small" onClick={() => verHistorial(v)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => {
                          setEdicion({ ...v, _que: 'vehiculo', tercero_id: v.tercero_id || '' });
                          setErrorForm('');
                        }}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {vehiculos.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 5,
                    color: 'text.disabled' }}>
                    No hay vehículos con esa búsqueda.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          /* El catálogo trae solo las marcas que de verdad entran a un
             lavadero. El resto no se pretende adivinar: se va agregando desde
             el mismo formulario de la orden a medida que aparezca. */
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
              Lo que se ofrece al registrar un vehículo. Si falta algo, se puede
              crear aquí o directamente en la orden, sin salir de la pantalla.
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
              {marcas.map(m => (
                <Paper key={m.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, flexGrow: 1 }}>
                      {m.nombre}
                    </Typography>
                    {m.vehiculos > 0 && (
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                        {m.vehiculos} vehículo{m.vehiculos === 1 ? '' : 's'}
                      </Typography>
                    )}
                    <Tooltip title="Renombrar">
                      <IconButton size="small" onClick={() => {
                        setEdicion({ ...m, _que: 'marca' }); setErrorForm('');
                      }}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Borrar marca">
                      <IconButton size="small" color="error" onClick={() => borrarMarca(m)}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {m.lineas.map(l => (
                      <Chip key={l.id} size="small" label={l.nombre}
                        onDelete={() => borrarLinea(m, l)}
                        sx={{ height: 22, fontSize: 11 }} />
                    ))}
                    <Chip size="small" icon={<AddIcon sx={{ fontSize: 14 }} />}
                      label="Línea" variant="outlined" color="primary"
                      onClick={() => agregarLinea(m)}
                      sx={{ height: 22, fontSize: 11 }} />
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Card>

      {/* Alta y edición */}
      <Dialog fullScreen={esMovil} open={Boolean(edicion)} onClose={() => setEdicion(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {edicion?._que === 'marca'
            ? `${edicion?.id ? 'Renombrar' : 'Nueva'} marca`
            : `${edicion?.id ? 'Editar' : 'Nuevo'} ${
                edicion?._que === 'empleado' ? 'empleado' : 'vehículo'}`}
        </DialogTitle>
        <DialogContent dividers>
          {errorForm && <Alert severity="error" sx={{ mb: 2 }}>{errorForm}</Alert>}
          {edicion?._que === 'empleado' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField size="small" label="Nombre *" fullWidth value={edicion.nombre}
                onChange={e => setEdicion({ ...edicion, nombre: e.target.value })} />
              <Stack direction={FILA} spacing={2}>
                {/* Texto libre: cada negocio tiene sus cargos (barbero,
                    mecánico, estilista, técnico…). */}
                <TextField size="small" label="Cargo" fullWidth value={edicion.cargo || ''}
                  placeholder="Barbero, técnico, estilista…"
                  onChange={e => setEdicion({ ...edicion, cargo: e.target.value })} />
                <TextField size="small" label="Documento" fullWidth value={edicion.documento || ''}
                  onChange={e => setEdicion({ ...edicion, documento: e.target.value })} />
              </Stack>
              <Stack direction={FILA} spacing={2}>
                <CampoTelefono etiqueta="Teléfono" fullWidth valor={edicion.telefono || ''} mostrarError={intento}
                  alCambiar={(v) => setEdicion({ ...edicion, telefono: v })} />
                <TextField select size="small" label="Sede" fullWidth value={edicion.sede_id || ''}
                  helperText="Vacío = rota entre sedes"
                  onChange={e => setEdicion({ ...edicion, sede_id: e.target.value })}>
                  <MenuItem value="">Todas</MenuItem>
                  {sedes.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
                </TextField>
              </Stack>
              <FormControlLabel control={
                <Switch checked={Boolean(edicion.activo)}
                  onChange={e => setEdicion({ ...edicion, activo: e.target.checked })} />
              } label="Activo — aparece al crear órdenes" />
            </Stack>
          )}
          {edicion?._que === 'marca' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField size="small" label="Marca *" fullWidth autoFocus
                value={edicion.nombre}
                helperText={edicion.id
                  ? 'Los vehículos de esta marca se actualizan solos'
                  : 'Las líneas se agregan después, con el botón «Línea»'}
                onChange={e => setEdicion({ ...edicion, nombre: e.target.value })} />
            </Stack>
          )}
          {edicion?._que === 'vehiculo' && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Placa *" sx={ancho(150)}
                  value={edicion.placa}
                  inputProps={{ style: { textTransform: 'uppercase', fontWeight: 700 } }}
                  onChange={e => setEdicion({ ...edicion, placa: e.target.value.toUpperCase() })} />
                <TextField select size="small" label="Tipo" fullWidth value={edicion.tipo || ''}
                  onChange={e => setEdicion({ ...edicion, tipo: e.target.value })}>
                  {TIPOS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction={FILA} spacing={2}>
                <SelectorMarcaLinea
                  marcas={marcas} marca={edicion.marca} linea={edicion.linea}
                  onCatalogo={cargarMarcas}
                  onChange={({ marca, linea }) => setEdicion({ ...edicion, marca, linea })} />
              </Stack>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Color" fullWidth value={edicion.color || ''}
                  onChange={e => setEdicion({ ...edicion, color: e.target.value })} />
                <TextField size="small" label="Modelo (año)" fullWidth value={edicion.modelo || ''}
                  onChange={e => setEdicion({ ...edicion, modelo: e.target.value })} />
              </Stack>
              <TextField select size="small" label="Cliente registrado" fullWidth
                value={edicion.tercero_id || ''}
                helperText="Si el dueño es un tercero del sistema"
                onChange={e => setEdicion({ ...edicion, tercero_id: e.target.value })}>
                <MenuItem value="">Sin registrar</MenuItem>
                {terceros.filter(t => t.es_cliente).map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.razon_social}</MenuItem>
                ))}
              </TextField>
              <Stack direction={FILA} spacing={2}>
                <TextField size="small" label="Propietario" fullWidth
                  value={edicion.propietario || ''}
                  helperText="Si no está registrado como tercero"
                  onChange={e => setEdicion({ ...edicion, propietario: e.target.value })} />
                <CampoTelefono etiqueta="Teléfono" fullWidth valor={edicion.telefono || ''} mostrarError={intento}
                  alCambiar={(v) => setEdicion({ ...edicion, telefono: v })} />
              </Stack>
              <TextField size="small" label="Notas" multiline rows={2} fullWidth
                value={edicion.notas || ''}
                helperText="Un rayón previo, el cliente pide sin cera…"
                onChange={e => setEdicion({ ...edicion, notas: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEdicion(null)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Historial del vehículo */}
      <Dialog fullScreen={esMovil} open={Boolean(historial)} onClose={() => setHistorial(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {historial?.vehiculo.placa}
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {[historial?.vehiculo.marca, historial?.vehiculo.color].filter(Boolean).join(' · ')}
            {' · '}{historial?.vehiculo.visitas} visita
            {historial?.vehiculo.visitas === 1 ? '' : 's'}
            {' · '}{fmt(historial?.vehiculo.total_facturado)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {historial?.ordenes.length === 0 && (
            <Typography sx={{ color: 'text.disabled', py: 3, textAlign: 'center' }}>
              Todavía no tiene órdenes.
            </Typography>
          )}
          {historial?.ordenes.map(o => (
            <Box key={o.id} sx={{ py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography sx={{ fontSize: 13, fontWeight: 700,
                                  fontFamily: 'ui-monospace, monospace' }}>
                  {o.numero}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {fecha(o.fecha)} · {o.sede}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{fmt(o.total)}</Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {o.servicios.join(' · ')}{o.atendio ? ` — ${o.atendio}` : ''}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setHistorial(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
